import { Link } from "react-router";
import { useRecordContext, useTranslate, useListContext } from "ra-core";
import { DataTable } from "@/components/admin/data-table";
import { DateField } from "@/components/admin/date-field";
import { FileField } from "@/components/admin/file-field";
import { NumberField } from "@/components/admin/number-field";
import { RecordField } from "@/components/admin/record-field";
import { ReferenceField } from "@/components/admin/reference-field";
import { ReferenceManyField } from "@/components/admin/reference-many-field";
import { Show } from "@/components/admin/show";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useIsMobile } from "@/hooks/use-mobile";
import { ContractStatusBadge } from "./ContractStatusBadge";
import { PaymentStatusBadge } from "../finance/planning/PaymentStatusBadge";
import { periodChoices } from "./ContractInputs";
import type { Contract, Payment } from "../types";

export const ContractShow = () => (
  <Show>
    <ContractShowContent />
  </Show>
);

const ContractShowContent = () => {
  const record = useRecordContext<Contract>();
  const translate = useTranslate();
  const isMobile = useIsMobile();
  if (!record) return null;

  const isOneTime      = record.payment_type === "one_time";
  const isSubscription = record.payment_type === "subscription";
  const isInstallment  = record.payment_type === "installment";

  const paymentTypeLabel = translate(
    `resources.contracts.payment_types.${record.payment_type}`,
    { _: record.payment_type }
  );
  const periodLabel = periodChoices.find((p) => p.id === record.period)?.name ?? record.period;

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_20rem]">
      <Card>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {/* Всегда */}
            <RecordField source="company_id">
              <ReferenceField source="company_id" reference="companies" link="show" />
            </RecordField>
            <RecordField source="contact_id">
              <ReferenceField source="contact_id" reference="contacts_summary" link="show" />
            </RecordField>
            {record.deal_id && (
              <RecordField source="deal_id">
                <ReferenceField source="deal_id" reference="deals" link="show" />
              </RecordField>
            )}
            <RecordField
              source="status"
              render={(c) => <ContractStatusBadge status={c.status} />}
            />
            <RecordField
              source="payment_type"
              render={() => <span>{paymentTypeLabel}</span>}
            />

            {/* Разовый */}
            {isOneTime && <RecordField source="total_amount" field={NumberField} />}
            {isOneTime && <RecordField source="start_date" field={DateField} />}

            {/* Подписка */}
            {isSubscription && (
              <RecordField
                source="monthly_amount"
                label="resources.contracts.fields.payment_amount"
                field={NumberField}
              />
            )}
            {isSubscription && record.period && (
              <RecordField
                source="period"
                label="resources.contracts.fields.period"
                render={() => <span>{periodLabel}</span>}
              />
            )}
            {isSubscription && <RecordField source="start_date" field={DateField} />}
            {isSubscription && record.end_date && <RecordField source="end_date" field={DateField} />}
            {isSubscription && record.next_payment_date && (
              <RecordField source="next_payment_date" field={DateField} />
            )}

            {/* Рассрочка */}
            {isInstallment && <RecordField source="total_amount" field={NumberField} />}
            {isInstallment && <RecordField source="start_date" field={DateField} />}
            {isInstallment && record.end_date && <RecordField source="end_date" field={DateField} />}
          </div>

          {record.notes && (
            <>
              <Separator />
              <RecordField
                source="notes"
                render={(c) => <span className="whitespace-pre-line">{c.notes}</span>}
              />
            </>
          )}
          {record.attachments?.length ? (
            <>
              <Separator />
              <RecordField source="attachments">
                <FileField
                  source="attachments"
                  src="src"
                  title="title"
                  target="_blank"
                  className="[&_a]:text-blue-600 [&_a]:hover:text-blue-800 [&_a]:underline [&_a]:break-all"
                />
              </RecordField>
            </>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-base font-medium">
              {translate("resources.payments.name", { smart_count: 2 })}
            </h3>
            <Button asChild size="sm">
              <Link to={`/payments/create?contract_id=${record.id}`}>
                {translate("resources.payments.action.new")}
              </Link>
            </Button>
          </div>
          <ReferenceManyField
            reference="payments"
            target="contract_id"
            sort={{ field: "planned_date", order: "ASC" }}
            empty={
              <p className="text-sm text-muted-foreground">
                {translate("resources.payments.empty.title")}
              </p>
            }
          >
            {isMobile ? <MobilePaymentRows /> : (
              <DataTable<Payment> rowClick="show" bulkActionButtons={false}>
                <DataTable.Col source="planned_date" field={DateField} />
                <DataTable.Col source="amount">
                  <NumberField source="amount" />
                </DataTable.Col>
                <DataTable.Col
                  source="status"
                  render={(payment) => <PaymentStatusBadge status={payment.status} />}
                />
              </DataTable>
            )}
          </ReferenceManyField>
        </CardContent>
      </Card>
    </div>
  );
};

const MobilePaymentRows = () => {
  const { data } = useListContext<Payment>();
  if (!data?.length) return null;

  return (
    <div className="space-y-2">
      {data.map((payment) => (
        <Link
          key={payment.id}
          to={`/payments/${payment.id}/show`}
          className="flex items-center justify-between rounded-lg border px-3 py-2 text-sm active:opacity-70"
        >
          <div className="flex flex-col gap-0.5">
            <span className="font-medium">
              {payment.amount
                ? Number(payment.amount).toLocaleString("ru-RU") + " ₽"
                : "—"}
            </span>
            {payment.planned_date && (
              <span className="text-xs text-muted-foreground">
                {new Date(payment.planned_date).toLocaleDateString("ru-RU")}
              </span>
            )}
          </div>
          <PaymentStatusBadge status={payment.status} />
        </Link>
      ))}
    </div>
  );
};
